/**
 * InsightsPanel - Displays intelligence pushed from OpenClaw
 * Polls GET /api/insights, shows severity badges, mark-as-read.
 * Empty state encourages OpenClaw connection.
 */
'use client';

import React, { useState } from 'react';
import { Lightbulb, AlertTriangle, CheckCircle, Info, ExternalLink, Bell, Check, X } from 'lucide-react';
import { usePolling } from '@/lib/usePolling';
import { formatRelative } from '@/lib/format';
import { Button } from '@/components/ui/Button';

interface Insight {
  id: string;
  source: string;
  agent_id?: string;
  category: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical' | 'success';
  action_url: string | null;
  created_at: string;
  read_at: string | null;
  acted_on_at?: string | null;
  dismissed_at?: string | null;
}

interface InsightsResponse {
  insights: Insight[];
  unread_count: number;
}

const SEVERITY_CONFIG = {
  info: {
    icon: Info,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    badge: 'bg-blue-500/20 text-blue-400',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    badge: 'bg-amber-500/20 text-amber-400',
  },
  critical: {
    icon: AlertTriangle,
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    badge: 'bg-rose-500/20 text-rose-400',
  },
  success: {
    icon: CheckCircle,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    badge: 'bg-emerald-500/20 text-emerald-400',
  },
};


export const InsightsPanel = () => {
  const [processingId, setProcessingId] = useState<string | null>(null);

  const { data, error } = usePolling<InsightsResponse>({
    url: '/api/insights?limit=10',
    intervalMs: 15000,
    transform: undefined,
    initialValue: { insights: [], unread_count: 0 },
  });

  const handleFeedback = async (id: string, action: 'acted_on' | 'dismissed') => {
    setProcessingId(id);
    try {
      await fetch(`/api/insights/${id}/feedback`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      // Removing optimistic update because usePolling doesn't expose mutate.
      // It will just re-fetch on the 15s interval.
    } catch (e) {
      console.error(e);
    } finally {
      setProcessingId(null);
    }
  };

  const insights = data.insights;
  const unreadCount = data.unread_count;

  return (
    <div className="bg-zinc-950 border border-white/5 rounded-2xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="p-4 border-b border-white/5 bg-zinc-900/30 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
          <span className="font-black text-zinc-500 text-[9px] uppercase tracking-widest">Intelligence Feed</span>
        </div>
        {unreadCount > 0 && (
          <div className="flex items-center gap-1.5">
            <Bell className="w-3 h-3 text-amber-400" />
            <span className="text-[9px] font-mono font-bold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
              {unreadCount} new
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
        {error && (
          <div className="text-amber-500 font-mono text-[10px] uppercase text-center py-2">
            Feed error
          </div>
        )}

        {insights.length === 0 && !error && (
          <div className="text-center py-8">
            <Lightbulb className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-500 text-xs font-mono uppercase tracking-wider mb-1">No insights yet</p>
            <p className="text-zinc-500 text-[10px]">Connect OpenClaw to push intelligence here</p>
          </div>
        )}

        {insights.map((insight) => {
          const config = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;
          const Icon = config.icon;
          const isRead = insight.read_at !== null;
          const isActedOn = !!insight.acted_on_at;
          const isDismissed = !!insight.dismissed_at;
          const isProcessing = processingId === insight.id;

          // Determine visual state based on feedback
          let containerClasses = `${config.border} ${config.bg}`;
          if (isDismissed) containerClasses = 'border-white/5 opacity-40 grayscale';
          else if (isActedOn) containerClasses = 'border-white/5 opacity-60';
          else if (isRead) containerClasses = 'border-white/5 opacity-80';

          return (
            <div
              key={insight.id}
              className={`border rounded-xl p-3 transition-colors ${containerClasses}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-1.5 rounded-lg ${isRead ? 'bg-zinc-900' : config.bg}`}>
                  <Icon className={`w-3.5 h-3.5 ${isRead ? 'text-zinc-500' : config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {insight.agent_id && (
                      <span className="text-[8px] font-black uppercase text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20 shadow-[0_0_8px_rgba(168,85,247,0.15)] flex items-center gap-1">
                        <Lightbulb className="w-2.5 h-2.5" />
                        {insight.agent_id}
                      </span>
                    )}
                    <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${config.badge}`}>
                      {insight.severity}
                    </span>
                    <span className="text-[8px] font-mono text-zinc-500 bg-zinc-900 px-1.5 py-0.5 rounded">
                      {insight.category}
                    </span>
                    <span className="text-[9px] font-mono text-zinc-500 ml-auto shrink-0">
                      {formatRelative(insight.created_at)}
                    </span>
                  </div>
                  <h4 className={`text-sm font-medium leading-snug mb-1 ${isRead ? 'text-zinc-500' : 'text-zinc-200'}`}>
                    {insight.title}
                  </h4>
                  <p className="text-[11px] text-zinc-500 leading-relaxed line-clamp-2 text-pretty">
                    {insight.body}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[8px] font-mono text-zinc-500 italic">
                      via {insight.source}
                    </span>
                    {insight.action_url && (
                      <a
                        href={insight.action_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[9px] font-mono text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                      >
                        <ExternalLink className="w-2.5 h-2.5" />
                        Action
                      </a>
                    )}

                    {!isActedOn && !isDismissed && (
                      <div className="flex items-center gap-1 ml-auto">
                        <Button
                          onClick={() => handleFeedback(insight.id, 'acted_on')}
                          disabled={isProcessing}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 hover:bg-emerald-500/20 text-zinc-400 hover:text-emerald-400 text-[9px] font-mono transition-colors disabled:opacity-30"
                          title="This was useful, I acted on it"
                        >
                          <Check className="w-3 h-3" />
                          Done
                        </Button>
                        <Button
                          onClick={() => handleFeedback(insight.id, 'dismissed')}
                          disabled={isProcessing}
                          className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-400 text-[9px] font-mono transition-colors disabled:opacity-30"
                          title="Not useful, don't show this again"
                        >
                          <X className="w-3 h-3" />
                          Dismiss
                        </Button>
                      </div>
                    )}

                    {isActedOn && (
                      <span className="text-[9px] font-mono text-emerald-500/50 flex items-center gap-1 ml-auto">
                        <Check className="w-2.5 h-2.5" /> Acted on
                      </span>
                    )}
                    {isDismissed && (
                      <span className="text-[9px] font-mono text-zinc-600 flex items-center gap-1 ml-auto">
                        <X className="w-2.5 h-2.5" /> Dismissed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
